import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useNotificationStore } from './notification';

const API_URL = import.meta.env.VITE_API_URL;

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  phone_number?: string;
  company_name?: string;
}

interface LoginResponse {
  status: 'success' | 'error';
  message?: string;
  data?: User;
}

interface CurrentUserResponse {
  status: 'success' | 'error';
  message?: string;
  data?: User;
}

interface RegisterResponse {
  status: 'success' | 'error' | 'warning';
  message?: string;
  data?: {
    user: User;
    email_sent: boolean;
  };
}

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter();
  const notificationStore = useNotificationStore();
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const pendingOtp = ref(false);
  const otpSession = ref<{ email: string; password: string } | null>(null);
  const otpError = ref<string | null>(null);

  // Initialize user from localStorage
  const storedUser = localStorage.getItem('user');
  if (storedUser) {
    try {
      user.value = JSON.parse(storedUser);
    } catch (e) {
      localStorage.removeItem('user');
    }
  }

  // Get CSRF token from cookies (only when needed)
  const getCsrfToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const login = async (email: string, password: string) => {
    loading.value = true;
    error.value = null;
    pendingOtp.value = false;
    otpSession.value = null;
    otpError.value = null;
    try {
      const token = getCsrfToken();
      const response = await fetch(`${API_URL}/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'X-CSRFToken': token })
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      const data = await response.json();
      if (data.otpRequired) {
        pendingOtp.value = true;
        otpSession.value = { email, password };
        return false;
      }
      if (data.data) {
        user.value = data.data;
        localStorage.setItem('user', JSON.stringify(data.data));
        notificationStore.showSuccess({
          title: 'Success',
          text: data.message || 'Login successful'
        });
        setTimeout(() => {
          router.push('/dashboard/default');
        }, 100);
        return true;
      } else {
        const errorMessage = data.message || 'Login failed';
        error.value = errorMessage;
        notificationStore.showError({
          title: 'Error',
          text: errorMessage
        });
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during login';
      error.value = errorMessage;
      notificationStore.showError({
        title: 'Error',
        text: errorMessage
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const verifyOtp = async (code: string) => {
    loading.value = true;
    otpError.value = null;
    try {
      const token = getCsrfToken();
      const response = await fetch(`${API_URL}/two-factor/verify-login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'X-CSRFToken': token })
        },
        credentials: 'include',
        body: JSON.stringify({ code }),
      });
      const data = await response.json();
      console.debug('OTP verify response:', data);
      if (response.ok) {
        // After OTP, fetch current user from /current/profile/
        const userRes = await fetch(`${API_URL}/profile/current/`, {
          credentials: 'include',
        });
        const userData = await userRes.json();
        if (userData.data) {
          user.value = userData.data;
          localStorage.setItem('user', JSON.stringify(userData.data));
        } else {
          otpError.value = userData.message || 'Failed to fetch user after OTP';
          notificationStore.showError({
            title: 'Error',
            text: otpError.value || 'Failed to fetch user after OTP'
          });
          return false;
        }
        pendingOtp.value = false;
        otpSession.value = null;
        notificationStore.showSuccess({
          title: 'Success',
          text: 'OTP verified, login successful'
        });
        setTimeout(() => {
          router.push('/dashboard/default');
        }, 100);
        return true;
      } else {
        otpError.value = data.detail || data.message || 'Invalid OTP';
        notificationStore.showError({
          title: 'Error',
          text: otpError.value || 'Invalid OTP'
        });
        return false;
      }
    } catch (err) {
      otpError.value = err instanceof Error ? err.message : 'An error occurred during OTP verification';
      notificationStore.showError({
        title: 'Error',
        text: otpError.value || 'An error occurred during OTP verification'
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    loading.value = true;
    error.value = null;
    let logoutAttemptFailed = false; // Flag to track backend logout attempt

    try {
      const token = getCsrfToken();
      if (!token) {
        console.warn('No CSRF token found for logout request, but proceeding with client-side cleanup.');
      }

      const response = await fetch(`${API_URL}/auth/logout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'X-CSRFToken': token })
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Log the error and notify, but don't stop client-side cleanup
        logoutAttemptFailed = true;
        const errorData = await response.json().catch(() => ({ message: 'Logout failed and failed to parse error response.' }));
        const errorMessage = errorData.message || `Logout failed with status: ${response.status}`;
        console.error('Backend logout failed:', errorMessage, errorData);
        error.value = errorMessage;
        // We will still proceed to clear client-side state
      }
    } catch (err) {
      logoutAttemptFailed = true;
      console.error('Error during logout API call:', err);
      error.value = err instanceof Error ? err.message : 'An error occurred during logout API call';
      // We will still proceed to clear client-side state
    }

    // Always perform client-side cleanup regardless of backend logout success
    user.value = null;
    localStorage.removeItem('user');
    
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
    }
    
    if (logoutAttemptFailed) {
      // If backend logout failed, show error notification but still redirect
      notificationStore.showError({
        title: 'Logout Issue',
        text: error.value || 'Could not log out from server, but local session cleared.'
      });
    } else {
      notificationStore.showSuccess({
        title: 'Success',
        text: 'Logout successful'
      });
    }

    // Redirect to login page
    // Adding a slightly longer timeout to ensure notifications can be seen if they pop up.
    setTimeout(() => {
      router.push('/auth/login');
    }, logoutAttemptFailed ? 1500 : 100); // Longer timeout if there was an error to read it.

    loading.value = false;
    return !logoutAttemptFailed; // Return true if backend logout was successful, false otherwise
  };

  const getCurrentUser = async () => {
    loading.value = true;
    error.value = null;
    try {
      const response = await fetch(`${API_URL}/auth/me/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (response.status === 403) {
        user.value = null;
        localStorage.removeItem('user');
        // Clear all cookies
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i];
          const eqPos = cookie.indexOf('=');
          const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
          document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${window.location.hostname}`;
        }
        throw new Error('Session expired or unauthorized');
      }

      const data: CurrentUserResponse = await response.json();

      if (data.status === 'success' && data.data) {
        user.value = data.data;
        localStorage.setItem('user', JSON.stringify(data.data));
        return true;
      } else {
        throw new Error(data.message || 'Failed to get current user');
      }
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'An error occurred while fetching user data';
      notificationStore.showError({
        title: 'Error',
        text: error.value
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    confirm_password: string;
    first_name: string;
    last_name: string;
    phone_number?: string;
    company_name?: string;
  }) => {
    loading.value = true;
    error.value = null;
    try {
      // Get current token from cookies
      const token = getCsrfToken();

      const response = await fetch(`${API_URL}/auth/register/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'X-CSRFToken': token })
        },
        credentials: 'include',
        body: JSON.stringify(userData),
      });

      const data: RegisterResponse = await response.json();

      if (data.status === 'success') {
        return true;
      } else if (data.status === 'warning') {
        notificationStore.showError({
          title: 'Warning',
          text: data.message || 'Registration completed with warnings'
        });
        return true;
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (err) {
      console.error('Registration error:', err);
      error.value = err instanceof Error ? err.message : 'An error occurred during registration';
      notificationStore.showError({
        title: 'Error',
        text: error.value
      });
      return false;
    } finally {
      loading.value = false;
    }
  };

  const clearError = () => {
    error.value = null;
  };

  const isAuthenticated = computed(() => {
    return !!user.value;
  });

  return {
    user,
    loading,
    error,
    login,
    logout,
    getCurrentUser,
    register,
    clearError,
    isAuthenticated,
    getCsrfToken,
    pendingOtp,
    otpSession,
    otpError,
    verifyOtp
  };
});
